import clsx from 'clsx'
import type { ComponentProps, CSSProperties, ElementType } from 'react'
import { type IconRootProps } from '../../icons/Root'
import { Button, type ButtonProps } from '../Button/Button'
import { Text } from '../Text/Text'
import { actionsContainerStyles, actionStyles, bodyStyles, iconStyles, messageStyles, rootStyles } from './Alert.css'

export type Variant = 'info' | 'infoInline' | 'warning' | 'error' | 'success'

type RootProps = {
  variant?: Variant
} & ComponentProps<'div'>

function Root({ children, className, variant, ...props }: RootProps) {
  return (
    <div className={clsx(rootStyles({ variant }), className)} {...props}>
      {children}
    </div>
  )
}

type IconProps = {
  icon: ElementType<IconRootProps>
  style?: CSSProperties
}

function Icon({ icon, ...props }: IconProps) {
  const IconComponent = icon
  return <IconComponent size="1rem" className={iconStyles} {...props} />
}

type BodyProps = ComponentProps<'div'>

function Body({ children, className, ...props }: BodyProps) {
  return (
    <div className={clsx(bodyStyles, className)} {...props}>
      {children}
    </div>
  )
}

type MessageProps = ComponentProps<typeof Text>

function Message({ children, className, ...props }: MessageProps) {
  return (
    <Text className={clsx(messageStyles, className)} size="xs" {...props}>
      {children}
    </Text>
  )
}

type ActionsProps = ComponentProps<'div'>

function Actions({ children, className, ...props }: ActionsProps) {
  return (
    <div className={clsx(actionsContainerStyles, className)} {...props}>
      {children}
    </div>
  )
}

type ActionProps = Partial<ButtonProps<'button'>>

function Action({ children, className, size = 'medium', ...props }: ActionProps) {
  return (
    <Button variant="secondary-alt" size={size} className={clsx(actionStyles, className)} {...props}>
      {children}
    </Button>
  )
}

export const Alert = {
  Root,
  Icon,
  Body,
  Message,
  Actions,
  Action,
}
